import React, { useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { Button, Tabs, Flex } from "antd"
import DateSwitcher from "modules/datepicker/DateSwitcher"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import useActiveGuide from "hooks/useActiveClient"
import { usePermissions } from "hooks/usePermissions"
import styles from "./index.module.css"
import { spacings } from "themes/variables"
import moment from "moment"

const DEFAULT_RANGE = "week"
const RANGE_OPTIONS = {
  date: "Day",
  week: "Week",
  month: "Month"
}

const CalendarHeader = ({
  params,
  onChangeFilters,
  showControls = { addBooking: true, journal: false, scheduleTimeOff: false },
  onAddBooking,
  onJournalClick,
  onScheduleTimeOffClick,
  bookingsData = [],
  variant
}) => {
  const { t } = useTranslation()
  const { isActive: active } = useActiveGuide()
  const { hasPermission } = usePermissions()
  const date = useMemo(() => (params.date ? global.dateTime(params.date) : null), [params.date])

  // Calculate upcoming bookings count (today and future bookings)
  const upcomingBookingsCount = useMemo(() => {
    const today = moment().startOf("day")
    return bookingsData.filter((booking) => {
      const bookingDate = moment(booking.formatted_date, "YYYY-MM-DD")
      return bookingDate.isSameOrAfter(today)
    }).length
  }, [bookingsData])

  useEffect(() => {
    if (!params.range) onChangeFilters({ target: { value: DEFAULT_RANGE, name: "range" } })
  }, [params.range]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!params.range) return null

  return (
    <Flex vertical>
      <Flex className={`px-3xl py-s ${styles.headerLegend}`} vertical gap={spacings.M}>
        <Flex justify="space-between">
          <Text size="xl" fw={600}>
            Upcoming Bookings <span className="text-tertiary-light">({upcomingBookingsCount})</span>
          </Text>

          {variant == "crew" && (
            <>
              {showControls.addBooking && (
                <Button
                  type="primary"
                  className={`${styles.newCrewBooking} px-m py-s`}
                  size="large"
                  onClick={active ? onAddBooking : undefined}
                  disabled={!active}
                  aria-disabled={!active}
                >
                  {t("bookings.add")}
                </Button>
              )}
            </>
          )}
        </Flex>
      </Flex>
      <Flex
        gap={10}
        align="center"
        justify="space-between"
        className={`px-xl py-m ${styles.calendarActionsWrapper} ${variant === "crew" ? styles.crewMemberCalendarHeader : ""}`}
        wrap="wrap"
      >
        <DateSwitcher
          className={styles.calendarSwitcher}
          value={date}
          defaultValue={global.dateTime().startOf("date")}
          name="date"
          onChange={onChangeFilters}
          type={params.range}
          isCompleteDateRange={true}
        />
        <Flex flex={1} justify="space-between" className={styles.tabsContainer}>
          <Tabs
            activeKey={params.range}
            onChange={(key) => onChangeFilters({ target: { name: "range", value: key } })}
            className={styles.antDTabs}
          >
            {Object.entries(RANGE_OPTIONS).map(([range, label]) => (
              <Tabs.TabPane tab={label} key={range} />
            ))}
          </Tabs>
          <Button
            color="primary"
            variant="outlined"
            size="middle"
            disabled={date && moment(params.date).isSame(moment(), "day")}
            onClick={() => onChangeFilters({ target: { name: "date", value: moment().format("YYYY-MM-DD") } })}
          >
            {t("calendar.go_to_today", "Go to Today")}
          </Button>
        </Flex>
        <Flex gap={10} className={`${styles.calendarActions} ml-auto`}>
          {showControls.addBooking && hasPermission("bookings", "create") && (
            <Button
              type="primary"
              className={`${styles.newBooking} px-m py-s`}
              size="large"
              onClick={active ? onAddBooking : undefined}
              disabled={!active}
              aria-disabled={!active}
            >
              {t("bookings.add")}
            </Button>
          )}
          {showControls.journal && (
            <Button
              color="primary"
              variant="outlined"
              className={`${styles.addJournal} px-m py-s`}
              size="large"
              onClick={active ? onJournalClick : undefined}
              disabled={!active}
              aria-disabled={!active}
            >
              {t("calendar.journal.add_journal")}
            </Button>
          )}
          {showControls.scheduleTimeOff && (
            <Button
              color="primary"
              variant="outlined"
              className={`${styles.scheduleTimeOff} px-m py-s`}
              size="large"
              onClick={active ? onScheduleTimeOffClick : undefined}
              disabled={!active}
              aria-disabled={!active}
            >
              {t("unavailable_days.schedule_time_off")}
            </Button>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

CalendarHeader.propTypes = {
  params: PropTypes.shape({
    date: PropTypes.string,
    range: PropTypes.string
  }).isRequired,
  onChangeFilters: PropTypes.func.isRequired,
  showControls: PropTypes.shape({
    addBooking: PropTypes.bool,
    journal: PropTypes.bool,
    scheduleTimeOff: PropTypes.bool
  }),
  onAddBooking: PropTypes.func,
  onJournalClick: PropTypes.func,
  onScheduleTimeOffClick: PropTypes.func,
  bookingsData: PropTypes.arrayOf(
    PropTypes.shape({
      formatted_date: PropTypes.string
    })
  ),
  variant: PropTypes.string
}

export default CalendarHeader
